/*
 * @Author: Zhouqi
 * @Date: 2026-01-05 14:49:44
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-01-05 14:52:47
 */
/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: 统一的文件上传 Hook（合并所有上传逻辑）
 */
import { useCallback, useRef } from 'react';

import { AIFunction } from '@/utils/const';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { getAudioDurationSeconds, getImageInfo, getVideoInfo } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { FileUploader } from '@/utils/uploader';
import { extractWebPFirstFrame, isWebPFile, isWebPSingleFrame } from '@/utils/webpUtils';

import { UploadConfig } from '../../config/types';
import { FILE_STATUS } from '../constants';
import { ExtendedUploadFile, FileUploadRule } from '../types';
import { isAudioFile, isImageFile, isVideoFile, updateOrAddFile } from '../utils';
import { useFileValidator } from './useFileValidator';

export interface UseUploadOptions {
  /** 文件列表 */
  fileList: ExtendedUploadFile[];
  /** 文件列表 ref */
  fileListRef: React.MutableRefObject<ExtendedUploadFile[]>;
  /** 文件列表变化回调 */
  onFileListChange: (
    updatedFile: ExtendedUploadFile,
    newFileList: ExtendedUploadFile[],
  ) => void;
  /** 校验规则 */
  rules?: FileUploadRule[];
  /** 校验失败回调 */
  onValidationFailed?: (error: string) => void;
  /** 上传配置（包含 dirSceneType） */
  config: UploadConfig;
  /** 文件类型限制 */
  accept?: string;
  /** 是否开启选择模式 */
  selectable?: boolean;
  /** 选中文件变化回调 */
  onSelectChange?: (file: ExtendedUploadFile | null) => void;
  /** 上传来源功能标识（用于埋点） */
  feature?: AIFunction;
  /** 是否启用埋点 */
  enableTracking?: boolean;
}

/**
 * 统一的文件上传 Hook
 * 封装所有上传相关的业务逻辑，支持多文件并发上传
 * 支持图片、视频、音频等多种文件类型
 */
export const useUpload = ({
  fileList,
  fileListRef,
  onFileListChange,
  rules,
  onValidationFailed,
  config,
  accept,
  selectable = false,
  onSelectChange,
  feature = AIFunction.Other,
  enableTracking = true,
}: UseUploadOptions) => {
  // 使用 Map 管理多个上传实例
  const uploaderMap = useRef<Map<string, FileUploader>>(new Map());

  // 使用文件校验 Hook
  const { validate } = useFileValidator({ rules, fileList, accept });

  // 删除文件处理
  const handleDeleteFile = useCallback(
    (file: ExtendedUploadFile) => {
      const currentList = fileListRef.current;
      const newFileList = currentList.filter((f) => f.uid !== file.uid);

      // 如果开启选择模式，并且删除的是选中的文件
      if (selectable && file.isSelected && newFileList.length > 0) {
        // 自动选中第一个文件
        newFileList[0].isSelected = true;
        onSelectChange?.(newFileList[0]);
      }

      // 取消上传并清理实例
      uploaderMap.current.get(file.uid)?.cancel();
      uploaderMap.current.delete(file.uid);
      onFileListChange(file, newFileList);
    },
    [fileListRef, onFileListChange, selectable, onSelectChange],
  );

  // 自定义上传请求
  const customRequest = useCallback(
    async (options: any) => {
      const { file, onSuccess, onError } = options;

      // 执行文件校验
      if (rules && rules.length > 0) {
        const validationResult = await validate(file);

        if (!validationResult.valid) {
          // 校验失败，调用回调并阻止上传
          onValidationFailed?.(validationResult.errors[0]);
          onError?.(new Error(validationResult.errors[0]));
          return;
        }
      }

      // WebP 动图处理：提取首帧
      let actualFile = file;
      try {
        const isWebp = await isWebPFile(file);
        if (isWebp) {
          const result = await isWebPSingleFrame(file);
          if (!result.isSingleFrame) {
            // 如果是动图，提取首帧作为实际上传的文件
            actualFile = await extractWebPFirstFrame(file);
          }
        }
      } catch (error) {
        console.error('WebP processing error:', error);
        // WebP 处理失败，使用原文件继续上传
      }

      const uploadingFile: ExtendedUploadFile = {
        uid: file.uid,
        name: file.name,
        status: FILE_STATUS.UPLOADING,
        size: file.size,
        type: file.type,
        originFileObj: file,
        uploadProgress: 0,
        isSelected: false,
      };

      // 使用 ref 获取最新的 fileList，避免批量上传时的竞态问题
      const uploadingList = [...fileListRef.current, uploadingFile];
      onFileListChange(uploadingFile, uploadingList);

      // 创建该文件的上传实例
      const uploader = FileUploader.getInstance({
        onProgress: (data) => {
          const progress = Math.round(data.progress * 100);

          // 更新该文件的上传进度
          const updatedFile: ExtendedUploadFile = {
            ...uploadingFile,
            uploadProgress: progress,
          };

          // 使用 ref 获取最新的 fileList
          const updatedList = updateOrAddFile(updatedFile, fileListRef.current);
          onFileListChange(updatedFile, updatedList);
        },
        onError: (error) => {
          // 统一处理上传错误
          const errorMessage = error.message || t('merror.tip.uploadFailed');
          const errorFile: ExtendedUploadFile = {
            ...uploadingFile,
            status: FILE_STATUS.ERROR,
            uploadProgress: 0,
            errorMessage,
          };

          // 使用 ref 获取最新的 fileList
          const errorList = updateOrAddFile(errorFile, fileListRef.current);
          onFileListChange(errorFile, errorList);
          onError?.(new Error(errorMessage));

          // 清理上传实例
          uploaderMap.current.delete(file.uid);
        },
        onSuccess: async (url) => {
          // 上传成功
          const successFile: ExtendedUploadFile = {
            ...uploadingFile,
            url,
            status: FILE_STATUS.SUCCESS,
            uploadProgress: 100,
          };

          // 使用 ref 获取最新的 fileList
          let successList = updateOrAddFile(
            successFile,
            fileListRef.current,
          ) as ExtendedUploadFile[];

          // 如果开启选择模式，并且是第一个成功上传的文件，自动选中
          if (selectable) {
            const hasSelected = successList.some((f) => f.isSelected);
            if (!hasSelected) {
              // 没有选中的，自动选中第一个上传成功的
              successList = successList.map((f) => ({
                ...f,
                isSelected: f.uid === successFile.uid,
              }));
              onSelectChange?.(successFile);
            }
          }

          onFileListChange(successFile, successList);
          onSuccess?.(url, file);

          // 清理上传实例
          uploaderMap.current.delete(file.uid);

          // 埋点上报（如果启用）
          if (enableTracking) {
            if (isImageFile(file)) {
              getImageInfo(url).then((info) => {
                const { width, height } = info;
                trackEvent(EventNewID.ASSET_UPLOAD, {
                  source: feature,
                  resolution: `${width}x${height}`,
                });
              });
            } else if (isVideoFile(file)) {
              getVideoInfo(url).then((info) => {
                const { duration, width, height } = info;
                trackEvent(EventNewID.ASSET_UPLOAD, {
                  source: feature,
                  duration: +duration,
                  resolution: `${width}x${height}`,
                });
              });
            } else if (isAudioFile(file)) {
              getAudioDurationSeconds(file).then((duration) => {
                trackEvent(EventNewID.ASSET_UPLOAD, {
                  source: feature,
                  duration,
                });
              });
            }
          }
        },
      });

      uploaderMap.current.set(file.uid, uploader);

      // 执行上传（使用处理后的文件）
      uploader.upload({
        file: actualFile,
        tokenData: {
          fileName: file.name,
          dirSceneType: config.dirSceneType,
        },
      });
    },
    [
      fileListRef,
      onFileListChange,
      rules,
      validate,
      onValidationFailed,
      config,
      selectable,
      onSelectChange,
      feature,
      enableTracking,
    ],
  );

  return {
    handleDeleteFile,
    customRequest,
  };
};
